import moment from 'moment/moment';
import RapidSearch from 'constants/rapidSearch';
import { options } from 'lib/ComparablesSorter';
import { getCompItemTitlePrice, getPropertyDistanceFromLocation, isNZLocale } from 'lib/PropertyUtils';
import { resolveDistance } from 'lib/distanceResolver';
import propertyTypes from 'constants/propertyTypes';
import listingTypes from 'constants/listingTypes';
import {
    COMMERCIAL_RANGE_LABELS,
    DISTANCE_LABELS,
    EXTERNAL_CHECKBOX_FILTER_LABELS,
    EXTERNAL_RANGE_FILTER_LABELS,
    FREQUENCY_VALUES,
    FRONTAGE_FILTER_LABELS,
    INTERNAL_CHECKBOX_FILTER_LABELS,
    INTERNAL_RANGE_FILTER_LABELS,
    RURAL_CHECKBOX_FILTER_LABELS,
    RURAL_RANGE_FILTER_LABELS,
    SERVICE_FILTER_LABELS
} from 'constants/filterOptions';
import { EXTERNAL_TEXTBOX_LABEL } from 'constants/advancedFilters';

const {OPERATORS} = RapidSearch;

const AGENT_NOT_WITHHELD = 'agent not withheld';
const AGENT_WITHHELD = 'agent withheld';

const isRapidAgentAdvice = salesLastSaleSource => !!salesLastSaleSource
    && (salesLastSaleSource.toLowerCase() === AGENT_NOT_WITHHELD
        || salesLastSaleSource.toLowerCase() === AGENT_WITHHELD);

export function transformRapidSearchToLegacyResponse(comparableType, response = {}, propertyId) {
    const {data = [], metadata: {limit, offset, totalElements, totalPages} = {}} = response;
    const items = data.map(p => ({
        ...p.core,
        ...p[comparableType],
    })).filter(p => p.propertyId !== propertyId);
    const lengthDiff = items.length - data.length;

    return {
        items,
        page: offset,
        size: limit + lengthDiff,
        totalItems: totalElements + lengthDiff,
        totalPages,
    };
}

const formatFilter = (field, operation, value) => ({field, operation, value});

const buildFilter = (field, operator, value) => field && value ? formatFilter(field, operator, value) : null;

const buildEqualFilter = (field, value) => buildFilter(field, OPERATORS.EQUALS_OPERATOR, value);

const radiusFormatBuilder = ({lat, lon} = {}, radius) =>
    lat && lon
        ? formatFilter(
            'location',
            OPERATORS.GEODISTANCE_OPERATOR,
            {lat, lon, radius},
        )
        : null;

const buildRange = (field, {min, max} = {}) => {
    if (!min && !max) return null;
    if (min !== undefined && max !== undefined && min === max) {
        return {
            operation: OPERATORS.AND_OPERATOR,
            filters: [
                buildFilter(field, OPERATORS.DOUBLE_EQUALS_OPERATOR, min),
            ],
        };
    }
    return {
        operation: OPERATORS.AND_OPERATOR,
        filters: [
            min ? buildFilter(field, OPERATORS.GREATER_THAN_OPERATOR, min) : null,
            max ? buildFilter(field, OPERATORS.LESS_THAN_OPERATOR, max) : null,
        ].filter(Boolean),
    };
};

const buildZeroAttributeFilterNz = (field) => buildFilter(field, OPERATORS.DOUBLE_EQUALS_OPERATOR, '0');

const buildZeroAttributeFilterAu = (field) => {
    return {
        operation: OPERATORS.AND_NOT_OPERATOR,
        filters: [{
            field,
            operation: OPERATORS.EXISTS_OPERATOR,
        }],
    };
};

/**
 * AU batch search should use this format below if the attribute value is 0
 * {
 *     "operation": "and not",
 *     "filters": [
 *         {
 *             "field": "attributeName",
 *             "operation": "exists"
 *         }
 *     ]
 * },
 * NZ batch search should this format below if the attribute value is 0
 * {
 *     "field": "attributeName",
 *     "operation": "==",
 *     "value": "0"
 * }
 */
const buildZeroAttributeFilter = (field) => isNZLocale()
    ? buildZeroAttributeFilterNz(field)
    : buildZeroAttributeFilterAu(field);

const buildAttributeFilter = (field, value = {}) => {
    const _value = JSON.parse(JSON.stringify(value));
    if (value?.max === '6+') _value.max = '';
    if (value?.min === '6+') _value.min = '6';

    if (_value?.min === '0' && _value?.max === '0') {
        return buildZeroAttributeFilter(field);
    }

    if (!_value?.max) {
        return buildFilter(field, OPERATORS.GREATER_THAN_OPERATOR, _value?.min);
    }

    if (_value?.min === '0' || _value?.min === '') {
        return {
            operation: OPERATORS.OR_OPERATOR,
            filters: [
                buildZeroAttributeFilter(field),
                buildFilter(field, OPERATORS.LESS_THAN_OPERATOR, _value?.max),
            ]
        };
    }
    return buildRange(field, _value);
};

const buildContainFilter = (field, value) => value && buildFilter(field, OPERATORS.CONTAINS_OPERATOR, value);

const buildAreaFilter = (field, {min, max, unit} = {}) => {
    if (!min && !max) return null;

    const parseValue = (value, unit) => {
        if (!value) return null;
        try {
            const cleanValue = parseFloat(value.toString().replace(/[^0-9.]+/g, '').trim());
            return unit === 'ha' ? cleanValue * 10000 : cleanValue;
        } catch {
            return value;
        }
    };

    const parsedMin = parseValue(min, unit);
    const parsedMax = parseValue(max, unit);

    const filters = [];

    if (parsedMin && parsedMax && parsedMin === parsedMax) {
        filters.push(buildFilter(field, OPERATORS.DOUBLE_EQUALS_OPERATOR, parsedMin));
    } else {
        if (parsedMin) filters.push(buildFilter(field, OPERATORS.GREATER_THAN_OPERATOR, parsedMin));
        if (parsedMax) filters.push(buildFilter(field, OPERATORS.LESS_THAN_OPERATOR, parsedMax));
    }

    return {
        operation: OPERATORS.AND_OPERATOR,
        filters,
    };
};

const buildPriceFilter = ({min, max} = {}, comparableType) => {
    const priceField = {
        sales: 'salesLastSoldPrice',
        listings: 'salesLastCampaignLastListedPrice',
        rentals: 'rentalLastCampaignLastListedPrice',
    }[comparableType];

    return buildRange(priceField, { min, max });
};

const buildTypeFilter = (selectedTypes, field, types) => {
    if (!selectedTypes) return;
    const allTypes = types.map(pt => pt.value);

    // If all types are selected or none are valid, do not set a filter
    if (selectedTypes.length === allTypes.length) {
        return null;
    }

    return {
        operation: OPERATORS.OR_OPERATOR,
        filters: selectedTypes.map(type => buildEqualFilter(field, type.toLowerCase())),
    };
};

const buildPropertyTypeFilter = (selectedTypes) => {
    return buildTypeFilter(selectedTypes, 'type', propertyTypes);
};

const buildListingTypeFilter = (selectedTypes) => {
    return buildTypeFilter(selectedTypes, 'salesLastCampaignListedType', listingTypes);
};

const buildTargetSuburbFilter = (targetSuburb, {
    localityName, addressState, postcode, addressTown,
}) => {
    if (targetSuburb) {
        if (isNZLocale()) {
            return [
                buildFilter('addressSuburb', OPERATORS.EQUALS_OPERATOR, localityName),
                buildFilter('addressTown', OPERATORS.EQUALS_OPERATOR, addressTown),
            ].filter(Boolean);
        } else {
            return [
                buildFilter('addressSuburb', OPERATORS.EQUALS_OPERATOR, localityName),
                buildFilter('addressPostcodeState', OPERATORS.EQUALS_OPERATOR, `${postcode} ${addressState}`),
            ].filter(Boolean);
        }
    }
    return [];
};

const buildCheckboxFilter = (field, value) => buildFilter(field, OPERATORS.IS_OPERATOR, value);
const buildActiveFilter = (comparableType, filters) => {
    const field = {
        listings: 'isActiveForSaleCampaign',
        rentals: 'isActiveForRentCampaign',
    };
    const isActiveField = filters[field[comparableType]];

    return isActiveField && buildFilter(field[comparableType], OPERATORS.IS_OPERATOR, isActiveField);
};

// Generic date builder for date range filters
const dateBuilder = (field, dateRangeStr, dateFormat = 'YYYY-MM-DD') => {
    const [fromStr, toStr] = dateRangeStr?.split(' - ')?.map(s => s.trim()) || [];
    const dateFrom = fromStr ? moment(fromStr, 'DD MMM YYYY').format(dateFormat) : null;
    const dateTo = toStr ? moment(toStr, 'DD MMM YYYY').format(dateFormat) : null;

    if (!dateFrom && !dateTo) return null;

    return {
        operation: OPERATORS.AND_OPERATOR,
        filters: [
            buildFilter(field, OPERATORS.DATE_FROM_OPERATOR, dateFrom),
            buildFilter(field, OPERATORS.DATE_TO_OPERATOR, dateTo),
        ].filter(Boolean),
    };
};

// For sale date, listing date, rental date
const buildDateFilter = (comparableType, dateRangeStr) => {
    if (!comparableType || !dateRangeStr) return null;

    const dateField = {
        sales: 'salesLastSaleContractDate',
        listings: 'salesLastCampaignEndDate',
        rentals: 'rentalLastCampaignEndDate',
    }[comparableType];

    return dateBuilder(dateField, dateRangeStr);
};

const buildExcludedTargetPropertyId = (propertyId) => propertyId && ({
    operation: OPERATORS.AND_NOT_OPERATOR,
    filters: [
        buildFilter('id', OPERATORS.EQUALS_OPERATOR, propertyId),
    ],
});

const buildPropertyIds = (propertyIds) => {
    if (propertyIds.length === 1) {
        return buildFilter('id', OPERATORS.EQUALS_OPERATOR, propertyIds[0]);
    } else if (propertyIds.length > 1) {
        return {
            operation: OPERATORS.OR_OPERATOR,
            filters: propertyIds
                .map(id => buildFilter('id', OPERATORS.EQUALS_OPERATOR, id))
                .filter(Boolean),
        };
    }
    return null;
};

const buildExternalFilters = (filters) => [
    ...Object.keys(EXTERNAL_CHECKBOX_FILTER_LABELS)
        .map(externalCheckboxType => buildCheckboxFilter(externalCheckboxType, filters[externalCheckboxType])),
    ...Object.keys(EXTERNAL_RANGE_FILTER_LABELS)
        .map(externalRangeType => buildRange(externalRangeType, filters[externalRangeType])),
    ...Object.keys(EXTERNAL_TEXTBOX_LABEL)
        .map(externalTextfieldType => buildContainFilter(externalTextfieldType, filters[externalTextfieldType]))
];

const buildInternalFilters = (filters) => [
    ...(Object.keys(INTERNAL_CHECKBOX_FILTER_LABELS))
        .map(internalCheckboxType => buildCheckboxFilter(internalCheckboxType, filters[internalCheckboxType])),
    ...(Object.keys(INTERNAL_RANGE_FILTER_LABELS)
        .map(internalRangeType => buildRange(internalRangeType, filters[internalRangeType]))),
    buildContainFilter('heritageOtherClassification', filters.heritageOtherClassification),
];

const buildFrontageFilters = (filters) => [
    ...(Object.keys(FRONTAGE_FILTER_LABELS)
        .map(frontageType => buildRange(frontageType, filters[frontageType]))),
];

const buildDistanceFilters = (filters) => [
    ...(Object.keys(DISTANCE_LABELS)
            .map(distanceType => buildRange(distanceType, filters[distanceType]))
    ),
];
const buildCommercialFilters = (filters) => [
    buildContainFilter('feeCode', filters.feeCode),
    ...(Object.keys(COMMERCIAL_RANGE_LABELS)
            .map(areaType => buildAreaFilter(areaType, filters[areaType]))
    ),
];

const buildRuralFilters = (filters) => [
    ...(Object.keys(RURAL_CHECKBOX_FILTER_LABELS)
        .map(ruralCheckboxType => buildCheckboxFilter(ruralCheckboxType, filters[ruralCheckboxType]))),
    ...(Object.keys(RURAL_RANGE_FILTER_LABELS)
        .map(ruralRangeType => buildRange(ruralRangeType, filters[ruralRangeType]))),
];

const buildServiceFilters = (filters) => [
    ...(Object.keys(SERVICE_FILTER_LABELS)
        .map(serviceFilterType => buildCheckboxFilter(serviceFilterType, filters[serviceFilterType]))),
];

const buildStructureFilter = (filters) => [
    buildAreaFilter('buildingArea', filters.buildingArea),
    buildAreaFilter('equivalentBuildingArea', filters.equivalentBuildingArea),
    buildRange('floorLevelOfUnit', filters.floorLevelOfUnit),
    buildRange('floorLevelsInsideUnit', filters.floorLevelsInsideUnit),
    buildRange('flats', filters.flats),
    buildRange('totalFloorsInBuilding', filters.totalFloorsInBuilding),
    buildRange('streetFrontage', filters.streetFrontage),
    buildRange('yearBuildingRefurbished', filters.yearBuildingRefurbished),
    buildRange('yearEstablished', filters.yearEstablished),
    buildCheckboxFilter('freeStandingBuilding', filters.freeStandingBuilding)
];

const buildPropertyValuesFilters = (filters) => [
    buildRange('currentUnimprovedLandValue', filters.currentUnimprovedLandValue),
    buildRange('unimprovedCapitalValue', filters.unimprovedCapitalValue),
    dateBuilder('unimprovedCapitalValueDate', filters.unimprovedCapitalValueDate),
    buildRange('latestConsumerSaleEstimate', filters.latestConsumerSaleEstimate),
    buildMultipleEqualFilter('latestConsumerSaleConfidence', filters.latestConsumerSaleConfidence),
];

const buildPropertyValuesNzFilters = (filters) => [
    buildRange('valuationCapitalValue', filters.valuationCapitalValue),
    dateBuilder('valuationAssessmentDate', filters.valuationAssessmentDate),
    buildRange('valuationLandValue', filters.valuationLandValue),
    buildRange('valuationImprovementsValue', filters.valuationImprovementsValue),
];
const buildExcludeRecentlyAdvisedSalesFilter = (value) => {
    return value && {
        operation: OPERATORS.OR_OPERATOR,
        filters: ['Valuer General', 'Valuer General Confirmed']
            .map(val => buildFilter('salesLastSaleSource', OPERATORS.EQUALS_OPERATOR, val))
    };
};

const buildExcludeRelatedParties = (value) => {
    return value && buildFilter('salesLastSaleIsArmsLength', OPERATORS.IS_OPERATOR, true);
};

const buildMultipleEqualFilter = (field, list) =>
    list?.length
        ? {operation: OPERATORS.OR_OPERATOR, filters: list.map(value => buildEqualFilter(field, value.toLowerCase()))}
        : null;

const aggregateTermBuilder = (field) => ({ field, method: 'term', size: 10000 });

const buildKeywordsFilter = (keywords) => keywords?.length
    ? {
        operation: OPERATORS.NESTED_OPERATOR,
        parentPath: 'latestPropertyCampaign',
        filter: {
            operation: OPERATORS.OR_OPERATOR,
            filters: keywords
                .map(value => buildContainFilter('latestPropertyCampaign.campaignDescription', value.toLowerCase())),
        },
    }
    : null;

const searchFilterBuilder = ({
    coordinates,
    sortBy,
    comparableType,
    page = 0,
    filters = {},
    addressState,
    addressTown,
    postcode,
    localityName,
    targetId,
    limit = 20,
    propertyIds = [],
    metadata = false,
}) => {
    const radius = radiusFormatBuilder(coordinates, filters.distance);
    const sortFn = options[sortBy]?.rapidSort ?? options.distance_asc.rapidSort;
    const sort = sortFn(comparableType);

    const filterList = [
        buildAttributeFilter('beds', filters.beds),
        buildAttributeFilter('baths', filters.baths),
        buildAttributeFilter('carSpaces', filters.cars),
        buildAreaFilter('landArea', filters.landArea),
        buildRange('floorArea', filters.floorArea),
        buildRange('yearBuilt', filters.yearBuilt),
        buildPriceFilter(filters.price, comparableType),
        buildEqualFilter('rentalLastCampaignLastListedPeriod', FREQUENCY_VALUES[filters.rentalPriceFrequency]),
        buildPropertyTypeFilter(filters.propertyType),
        buildListingTypeFilter(filters.listingType),
        buildActiveFilter(comparableType, filters),
        buildDateFilter(comparableType, filters.date),
        dateBuilder('salesLastSaleSettlementDate', filters.settlementDate),
        dateBuilder('salesLastCampaignStartDate', filters.firstListingDate),
        dateBuilder('rentalLastCampaignStartDate', filters.rentalLastCampaignStartDate),
        buildContainFilter('salesClassification', filters.salesClassification),
        buildExcludeRecentlyAdvisedSalesFilter(filters.excludeRecentlyAdvisedSales),
        buildExcludeRelatedParties(filters.excludeRelatedParties),
        buildMultipleEqualFilter('subType', filters.propertySubType),
        buildMultipleEqualFilter('developmentZoneDescription', filters.devZone),
        buildMultipleEqualFilter('landUse', filters.landUse),
        buildKeywordsFilter(filters.keywords),
        ...buildCommercialFilters(filters),
        ...buildExternalFilters(filters),
        ...buildInternalFilters(filters),
        ...buildFrontageFilters(filters),
        ...buildDistanceFilters(filters),
        ...buildRuralFilters(filters),
        ...buildServiceFilters(filters),
        ...buildStructureFilter(filters),
        ...(isNZLocale() ? buildPropertyValuesNzFilters(filters) : buildPropertyValuesFilters(filters)),

        // Making sure target property is not part of the results
        buildExcludedTargetPropertyId(targetId),
    ].filter(Boolean);

    return {
        requests: [{
            resultsFormat: {
                offset: page,
                limit,
                metadata,
                sort,
            },
            filters: [
                // Base filters goes here
                radius,
                ...buildTargetSuburbFilter(filters.targetSuburbOnly, {
                    localityName,
                    addressState,
                    postcode,
                    addressTown,
                }),
                // Used to get details for list of property ids
                buildPropertyIds(propertyIds),
                // Filters goes here
                filterList.length
                    ? {operation: OPERATORS.AND_OPERATOR, filters: filterList}
                    : null
            ].filter(Boolean),
        }],
    };
};

const searchWithAggregateBuilder = (filters) => {
    const formattedFilters = searchFilterBuilder(filters);
    return {
        requests: [{
            filters: formattedFilters.requests[0].filters,
            aggregations: [
                aggregateTermBuilder('subType'),
                aggregateTermBuilder('landUse'),
                aggregateTermBuilder('developmentZoneDescription'),
            ],
        }],
    };
};

const formatRapidResponseToLegacy = (response, comparableType, targetCoords) => {
    if (!response) return {};

    const newData = response?.data?.map(datum => {
        const {
            id,
            addressComplete,
            addressLocation,
            type,
            beds,
            baths,
            distance: rapidDistance,
            salesLastSoldPriceIsWithheld,
            salesLastCampaignFirstListedPriceDescription,
            salesLastCampaignLastListedPriceDescription,
            [RapidSearch.DATE_FIELD[comparableType]]: date,
            [RapidSearch.PRICE_FIELD[comparableType]]: price,
            [RapidSearch.DOM_FIELD[comparableType]]: dom,
            salesLastSaleSource,
            imageUrls,
            owners,
            addressSuburb,
            valuationCapitalValue,
            valuationAssessmentDate,
            ...rest
        } = datum || {};

        const comparableCoords = addressLocation
            ? { lat: addressLocation.lat, lon: addressLocation.lon }
            : null;
        const calculatedDistance = targetCoords && comparableCoords
            ? getPropertyDistanceFromLocation(targetCoords, comparableCoords)
            : null;
        const distance = resolveDistance(rapidDistance, calculatedDistance);

        return {
            ...rest,
            propertyId: id,
            singleLineAddress: addressComplete,
            latitude: addressLocation?.lat,
            longitude: addressLocation?.lon,
            propertyType: type,
            bathrooms: baths,
            bedrooms: beds,
            isPriceWithheld: salesLastSoldPriceIsWithheld,
            salePrice: price,
            saleDate: date,
            daysOnMarket: dom,
            distance,
            firstPublishedPrice: salesLastCampaignFirstListedPriceDescription,
            latestPublishedPrice: salesLastCampaignLastListedPriceDescription,
            owners: owners
                ?.map(({firstName, lastName} = {}) => [firstName, lastName]
                    ?.filter(Boolean)
                    ?.join(' ')),
            propertyPhotoUri: imageUrls?.largeImageUrl,
            localityName: addressSuburb,
            isAgentsAdvice: isRapidAgentAdvice(salesLastSaleSource),
            capitalValue: valuationCapitalValue,
            capitalValueDate: valuationAssessmentDate,
        };
    });

    return {
        data: newData,
        metadata: response?.metadata,
    };
};

// Price and date fields are renamed in the application
// to use a single naming convention regardless of the report type.
// "salePrice" and "saleDate";
// The API expects "latestPublishedPrice" and "listingDate"
// for listing and rental that is why we sanitize the listings here
const sanitizeListing = (comparableType, listing) => {
    const {salePrice, saleDate, ...rest} = listing;
    return {
        ...rest,
        latestPublishedPrice: getCompItemTitlePrice(comparableType, listing), // This is for the comparable cards
        listingPrice: getCompItemTitlePrice(comparableType, listing), // this is for the comparable map
        listingDate: saleDate,
    };
};

const sanitizeListings = (comparableType, listings) =>
    listings?.map(listing => sanitizeListing(comparableType, listing));

const RapidUtils = {
    searchFilterBuilder,
    formatRapidResponseToLegacy,
    sanitizeListing,
    sanitizeListings,
    searchWithAggregateBuilder,
};

export default RapidUtils;
